import type { Metadata } from 'next';

/** The brand fields rebranding needs — a subset of TenantBrand, so this stays client-safe. */
export interface RebrandBrand {
  isDefault: boolean;
  name: string;
  supportEmail?: string;
  /** `https://<request host>` — where canonical / OG page URLs must point. */
  origin?: string;
}

/** Page addresses on BookItPlease's site; moved onto the tenant's origin in metadata. */
const BIP_ORIGIN = /^https?:\/\/(?:www\.)?bookitplease\.com(?=\/|$)/i;
/** Metadata keys that hold the PAGE's own address (not an image or asset). */
const PAGE_URL_KEYS = new Set(['canonical', 'url']);

/**
 * BookItPlease's name as it appears in copy and metadata ("BookItPlease", "Book It Please",
 * "Bookitplease", "Bookitplease.com"). Not inside a URL or hostname: never preceded by
 * `.` `/` `-` `@`, never followed by `.com/` or `-` — so image hosts (`www.bookitplease.com/…`),
 * `sand-bookitplease…` and email addresses are left to BIP_EMAIL / untouched.
 */
const BIP_NAME = /(?<![./@-])\b(?:Book ?It ?Please|Bookitplease)(?:\.com)?\b(?!\.com\/|-|@)/gi;
const BIP_EMAIL = /\b(?:reservations|support)@bookitplease\.com\b/gi;

/**
 * Copy written for BookItPlease, for the current tenant.
 *
 * BookItPlease gets its text back byte-for-byte (its SEO titles and copy must not change);
 * any other tenant gets its own name, and its support email (or "our support team" when it has
 * none — never BookItPlease's address).
 */
export function rebrand(text: string, brand: RebrandBrand): string {
  if (brand.isDefault || !text) return text;
  return text
    .replace(BIP_EMAIL, brand.supportEmail ?? 'our support team')
    .replace(BIP_NAME, brand.name);
}

/** A `mailto:` href for the tenant's support address, or undefined when it has none. */
export function supportMailto(brand: RebrandBrand, bipAddress = 'reservations@bookitplease.com'): string | undefined {
  const address = brand.isDefault ? bipAddress : brand.supportEmail;
  return address ? `mailto:${address}` : undefined;
}

/** The support address to display (BookItPlease's own on BookItPlease). */
export function supportEmail(brand: RebrandBrand, bipAddress = 'reservations@bookitplease.com'): string {
  return brand.isDefault ? bipAddress : (brand.supportEmail ?? 'our support team');
}

function deep<T>(value: T, brand: RebrandBrand, key?: string): T {
  if (typeof value === 'string') {
    if (/^https?:\/\//i.test(value)) {
      // A page address moves to the tenant's own origin (SEO must not point at BookItPlease);
      // any other absolute URL (images, assets) is left as it is.
      return (key && PAGE_URL_KEYS.has(key) && brand.origin ? value.replace(BIP_ORIGIN, brand.origin) : value) as T;
    }
    return rebrand(value, brand) as T;
  }
  if (Array.isArray(value)) return value.map((v) => deep(v, brand)) as T;
  if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, deep(v, brand, k)])) as T;
  }
  return value;
}

/** Every string in a Metadata object (titles, templates, descriptions, OG…), rebranded. */
export function rebrandMetadata(metadata: Metadata, brand: RebrandBrand): Metadata {
  return brand.isDefault ? metadata : deep(metadata, brand);
}
